import React, { FC, MouseEvent } from 'react'
import classNames from 'classnames'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Box, makeStyles, Skeleton } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { BellIcon, DeleteIcon } from 'transfix-icons'
import { Card } from 'transfix-ui'
import { BookableShipmentSavedSearchAlertLocations } from './BookableShipmentSavedSearchAlertLocations'
import { BookableShipmentSavedSearchAlertRowLoadCounts } from './BookableShipmentSavedSearchAlertRowLoadCounts'
import { BookableShipmentSavedSearchAlertRowSummary } from './BookableShipmentSavedSearchAlertRowSummary'

interface BookableShipmentSavedSearchAlertRowItemsProps {
  savedSearch?: Partial<BookableShipmentSavedSearch>
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void
  /**
   * Conditionally render items depending on value of isSharedSavedSearch
   * @default false
   */
  isSharedSavedSearch?: boolean
}

const useStyles = makeStyles(theme => ({
  root: {
    '& div, & article': {
      lineHeight: 1,
    },
  },
  hover: {
    '&:hover': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  divider: {
    height: '36px',
    width: '1px',
    backgroundColor: brandColors.coolGray4,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}))

export const BookableShipmentSavedSearchAlertRowItems: FC<BookableShipmentSavedSearchAlertRowItemsProps> = ({
  isSharedSavedSearch = false,
  onDelete,
  savedSearch,
}) => {
  const { isMobile, isTablet } = useBreakpointContext()
  const classes = useStyles()
  const { id } = savedSearch || {}

  const bellIcon = (
    <Box mr={2}>
      <BellIcon size='large' color='coolGray8' />
    </Box>
  )

  const deleteSavedSearchHandler = (event: MouseEvent<HTMLButtonElement>) => {
    if (onDelete) onDelete(event)
  }

  let locations
  let summary
  let alerts
  let divider
  let deleteButton
  if (savedSearch) {
    locations = <BookableShipmentSavedSearchAlertLocations savedSearch={savedSearch} />
    summary = <BookableShipmentSavedSearchAlertRowSummary savedSearch={savedSearch} />
    alerts = <BookableShipmentSavedSearchAlertRowLoadCounts savedSearch={savedSearch} />
    divider = <div className={classes.divider} />
    deleteButton = (
      <IconButton
        color='tertiary'
        data-test={`saved-search-delete-button-${id}`}
        Icon={DeleteIcon}
        onClick={deleteSavedSearchHandler}
        size='large'
      />
    )
  } else {
    const placeholder = <Skeleton variant='rectangular' height='36px' width='75%' />
    locations = placeholder
    summary = placeholder
    alerts = <></>
    divider = <></>
    deleteButton = <></>
  }

  const body =
    isMobile || isTablet ? (
      <Box p={1} display='flex' alignItems='center' justifyContent='space-between'>
        <Box display='flex'>
          {!isSharedSavedSearch && <Box p={1}>{bellIcon}</Box>}
          <Box flexDirection='column' alignItems='baseline'>
            {locations}
            {summary}
            {!isSharedSavedSearch && <Box py={1}>{alerts}</Box>}
          </Box>
        </Box>
        {!isSharedSavedSearch && <Box>{deleteButton}</Box>}
      </Box>
    ) : (
      <Box
        data-test={`saved-search-row-${id}`}
        display='flex'
        alignItems='center'
        justifyContent='space-between'>
        {/* Left Column */}
        <Box display='flex' alignItems='center' width='40%' pl={1}>
          {!isSharedSavedSearch && bellIcon}
          {locations}
        </Box>
        {/* Center Column */}
        <Box display='flex' alignItems='center' width='30%' mr='auto'>
          {summary}
        </Box>
        {/* Right Column */}
        {!isSharedSavedSearch && (
          <Box display='flex' alignItems='center'>
            {alerts}
            {divider}
            {deleteButton}
          </Box>
        )}
      </Box>
    )

  return (
    <Card
      noPadding
      className={classNames(classes.root, {
        [classes.hover]: !isSharedSavedSearch,
      })}>
      <Box p={1}>{body}</Box>
    </Card>
  )
}
